import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { STRATEGY_LABELS } from './constants'
import { useUpdateCampaign } from './use-campaigns'
import type { Campaign, OutreachStrategy } from './types'

type EditCampaignDialogProps = {
  readonly campaign: Campaign
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
}

export function EditCampaignDialog({
  campaign,
  open,
  onOpenChange,
}: EditCampaignDialogProps) {
  const [name, setName] = useState(campaign.name)
  const [strategy, setStrategy] = useState<OutreachStrategy>(
    campaign.sequence_config.steps[0]?.strategy ?? 'mutual_value',
  )

  const updateCampaign = useUpdateCampaign()

  function handleSave() {
    // daily_limit is no longer user-editable (2026-07-27 owner directive) —
    // the platform manages sending volume; the stored value stays as-is.
    updateCampaign.mutate(
      {
        id: campaign.id,
        updates: {
          name,
          sequence_config: {
            ...campaign.sequence_config,
            steps: campaign.sequence_config.steps.map((step) => ({
              ...step,
              strategy,
            })),
          },
        },
      },
      {
        onSuccess: () => onOpenChange(false),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Campaign</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="campaign-name">Name</Label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Campaign name"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="campaign-strategy">Strategy</Label>
            <Select
              value={strategy}
              onValueChange={(v) => setStrategy(v as OutreachStrategy)}
            >
              <SelectTrigger id="campaign-strategy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STRATEGY_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={updateCampaign.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateCampaign.isPending || !name.trim()}
          >
            {updateCampaign.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
